import {
  type BillingPeriod,
  SHARE_CASHBACK_RULES,
  SUBSCRIPTION_TIER_CREDITS,
  type SubscriptionTier,
} from '@pulsify/shared/dto'
import { Link } from '@tanstack/react-router'
import { Coins } from 'lucide-react'
import { requestCreditTopUp } from '~/entities/credits/model/credit-top-up'
import { useCreditPack } from '~/entities/credits/model/use-credit-pack'
import { useCreditsBalance } from '~/entities/credits/model/use-credits'
import { useCashback } from '~/entities/share-link/model/use-share-links'
import { billingPeriodLabel, formatCredits, planName } from '~/entities/subscription/plans'
import * as m from '~/paraglide/messages'
import { getLocale } from '~/paraglide/runtime.js'
import { Badge } from '~/shared/ui/badge'
import { Button } from '~/shared/ui/button'
import { Card } from '~/shared/ui/card'
import { Skeleton } from '~/shared/ui/skeleton'
import { BillingCell } from './billing-cell'

interface BillingOverviewProps {
  businessId: string
  tier: SubscriptionTier
  billingPeriod: BillingPeriod | null
}

/** Plan, credits and cashback at a glance, with the one action each of them needs. */
export function BillingOverview({ businessId, tier, billingPeriod }: BillingOverviewProps) {
  const { data: credits, isLoading: creditsLoading } = useCreditsBalance(businessId)
  const { data: cashback, isLoading: cashbackLoading } = useCashback(businessId)
  const pack = useCreditPack()
  const offerEnd = cashback
    ? new Intl.DateTimeFormat(getLocale(), {
        timeZone: 'Europe/Vienna',
        day: 'numeric',
        month: 'numeric',
      }).format(new Date(Date.parse(cashback.offer.endsAt) - 1))
    : null

  return (
    <Card className="grid divide-y md:grid-cols-[1fr_1.4fr_1fr] md:divide-x md:divide-y-0">
      <BillingCell label={m.billingOverviewPlan()}>
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-semibold text-2xl tracking-[-0.02em]">{planName(tier)}</span>
          {tier !== 'free' && billingPeriod ? (
            <Badge variant="secondary">{billingPeriodLabel(billingPeriod)}</Badge>
          ) : null}
        </div>
        {tier === 'free' ? (
          <p className="text-muted-foreground text-sm">{m.billingOverviewFreeHint()}</p>
        ) : (
          <Link
            to="/dashboard/settings"
            className="text-muted-foreground text-sm underline-offset-4 hover:text-foreground hover:underline"
          >
            {m.managePlan()}
          </Link>
        )}
      </BillingCell>

      <BillingCell
        label={m.creditsUnit()}
        info={`${m.creditsMonthlyDescription({
          free: formatCredits(SUBSCRIPTION_TIER_CREDITS.free),
          verein: formatCredits(SUBSCRIPTION_TIER_CREDITS.verein_s),
          basic: formatCredits(SUBSCRIPTION_TIER_CREDITS.basic),
          premium: formatCredits(SUBSCRIPTION_TIER_CREDITS.premium),
        })} ${m.creditsSpendOrder()}`}
        action={
          pack ? (
            <Button type="button" size="sm" onClick={() => requestCreditTopUp()}>
              <Coins />
              {m.creditPackTitle()}
            </Button>
          ) : null
        }
      >
        {creditsLoading ? (
          <Skeleton className="h-8 w-28" />
        ) : (
          <span className="font-semibold text-2xl tabular-nums tracking-[-0.02em]">
            {formatCredits(credits?.credits ?? 0)}
          </span>
        )}
        <p className="text-muted-foreground text-sm">
          {credits
            ? [
                `${formatCredits(credits.breakdown.monthly)} ${m.creditsPartMonthly()}`,
                `${formatCredits(credits.breakdown.cashback)} ${m.creditsPartCashback()}`,
                `${formatCredits(credits.breakdown.extra)} ${m.creditsPartExtra()}`,
              ].join(' · ')
            : m.creditsRefillShort()}
        </p>
      </BillingCell>

      <BillingCell
        label={m.creditsPartCashback()}
        info={m.cashbackDescription({ days: SHARE_CASHBACK_RULES.expiryDays })}
      >
        {cashbackLoading ? (
          <Skeleton className="h-8 w-20" />
        ) : (
          <span className="font-semibold text-2xl tabular-nums tracking-[-0.02em]">
            {formatCredits(cashback?.activeCredits ?? 0)}
          </span>
        )}
        <p className="text-muted-foreground text-sm">
          {cashback && offerEnd
            ? cashback.offer.active
              ? m.cashbackOfferUntil({ date: offerEnd })
              : m.shareOfferOver()
            : null}
        </p>
      </BillingCell>
    </Card>
  )
}
